import { Link } from "react-router-dom";
import { TwitterLogo, LinkedinLogo, FacebookLogo, InstagramLogo, YoutubeLogo, WhatsappLogo, Phone } from "@phosphor-icons/react";
import BrandMark from "./BrandMark";
import MapleLeaf from "./MapleLeaf";
import { useSettings } from "@/lib/settings";
import { trackConversion } from "@/lib/track";

const SOCIAL_ICONS = {
  twitter: TwitterLogo,
  linkedin: LinkedinLogo,
  facebook: FacebookLogo,
  instagram: InstagramLogo,
  youtube: YoutubeLogo,
};

export default function Footer() {
  const s = useSettings();
  const social = s.social || {};
  return (
    <>
    <footer data-testid="site-footer" className="bg-gos-navy text-gos-bone mt-32">
      <div className="container-gos py-16 grid grid-cols-1 md:grid-cols-4 gap-10">
        <div className="md:col-span-1">
          <div className="mb-4"><BrandMark variant="light" size="lg" testid="footer-brand" /></div>
          {s.established_year && (
            <div data-testid="footer-since" className="text-[10px] tracking-[0.22em] uppercase text-gos-sand font-medium mb-3 flex items-center gap-1.5">
              <MapleLeaf size={11} className="text-gos-maple" /> Since {s.established_year}
            </div>
          )}
          <p className="text-sm text-gos-bone/70 max-w-xs">{s.description}</p>
          {(s.support_email || s.secondary_email || s.phone) && (
            <div className="mt-5 text-xs text-gos-bone/70 space-y-1">
              {s.support_email && <div><a href={`mailto:${s.support_email}`} className="hover:text-gos-sand">{s.support_email}</a></div>}
              {s.secondary_email && <div><a href={`mailto:${s.secondary_email}`} className="hover:text-gos-sand">{s.secondary_email}</a></div>}
              {s.phone && <div><a href={`tel:${s.phone.replace(/[^+0-9]/g, "")}`} className="hover:text-gos-sand">{s.phone}</a></div>}
            </div>
          )}
          <div className="mt-4 flex gap-2">
            {Object.entries(social).map(([key, url]) => {
              if (!url) return null;
              const Icon = SOCIAL_ICONS[key];
              if (!Icon) return null;
              return (
                <a key={key} href={url} target="_blank" rel="noreferrer" data-testid={`social-${key}`}
                  className="w-8 h-8 border border-white/15 hover:bg-gos-sand hover:border-gos-sand hover:text-gos-navy text-gos-bone flex items-center justify-center transition-colors">
                  <Icon size={14} weight="bold" />
                </a>
              );
            })}
          </div>
        </div>

        <div>
          <div className="overline text-gos-sand mb-4">Platform</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/countries" className="hover:text-gos-sand">Countries</Link></li>
            <li><Link to="/compare" className="hover:text-gos-sand">Compare</Link></li>
            <li><Link to="/services" className="hover:text-gos-sand">Services</Link></li>
            <li><Link to="/calculator" className="hover:text-gos-sand">Calculator</Link></li>
          </ul>
        </div>

        <div>
          <div className="overline text-gos-sand mb-4">Resources</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/case-studies" className="hover:text-gos-sand">Case Studies</Link></li>
            <li><Link to="/blog" className="hover:text-gos-sand">Insights</Link></li>
            <li><Link to="/contact" className="hover:text-gos-sand">Contact</Link></li>
            {s.terms_url && <li><a href={s.terms_url} target="_blank" rel="noreferrer" className="hover:text-gos-sand">Terms</a></li>}
            {s.privacy_url && <li><a href={s.privacy_url} target="_blank" rel="noreferrer" className="hover:text-gos-sand">Privacy</a></li>}
          </ul>
        </div>

        <div>
          <div className="overline text-gos-sand mb-4">Account</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/login" className="hover:text-gos-sand">Sign In</Link></li>
            <li><Link to="/register" className="hover:text-gos-sand">Create Account</Link></li>
            <li><Link to="/portal" className="hover:text-gos-sand">Client Portal</Link></li>
            <li><Link to="/admin" className="hover:text-gos-sand">Admin</Link></li>
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="container-gos py-6 text-xs text-gos-bone/60 flex flex-col md:flex-row gap-4 md:justify-between">
          <div data-testid="footer-copyright">{s.footer_text || `© ${new Date().getFullYear()} ${s.brand_name}. All Rights Reserved.`}</div>
          <div className="max-w-3xl leading-relaxed">
            {s.brand_name} is a business setup and support company. We are not a law firm, accounting firm, financial institution, or immigration consulting practice. Regulated services are referred to licensed partners in our network.
          </div>
        </div>
      </div>
    </footer>
    {/* Floating WhatsApp + phone CTAs (tracked) */}
    <FloatingContact settings={s} />
    </>
  );
}

function FloatingContact({ settings: s }) {
  const wa = (s.whatsapp_number || "").replace(/\D/g, "");
  if (!wa && !s.phone) return null;
  return (
    <div data-testid="floating-contact" className="fixed bottom-5 right-5 z-40 flex flex-col gap-2">
      {wa && (
        <a href={`https://wa.me/${wa}?text=${encodeURIComponent("Hi, I'd like to learn more about Open Business Anywhere.")}`}
          target="_blank" rel="noreferrer" data-testid="floating-whatsapp"
          onClick={() => trackConversion("whatsapp_click", { value: 10, payload: { source: "floating" } })}
          className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full w-12 h-12 flex items-center justify-center shadow-lg transition-colors"
          aria-label="Chat on WhatsApp">
          <WhatsappLogo size={24} weight="fill" />
        </a>
      )}
      {s.phone && (
        <a href={`tel:${s.phone.replace(/\s+/g, "")}`} data-testid="floating-phone"
          onClick={() => trackConversion("phone_click", { value: 10, payload: { source: "floating" } })}
          className="bg-gos-navy hover:bg-gos-navy/90 text-white rounded-full w-12 h-12 flex items-center justify-center shadow-lg transition-colors"
          aria-label="Call us">
          <Phone size={22} weight="duotone" />
        </a>
      )}
    </div>
  );
}
